import { ARIA_LABELS, BUTTON_LABELS, FEATURE_FLAGS } from '@/constants';
import { cn } from '@/utils/cn';
import { getButtonStyles } from './ShareWithManagerButton.styles';
import { Icon } from '@/components/Icons/Icon';
import { ShareWithManagerButtonProps } from './types';
import { useShareWithManagerFeature } from '@/features/share-with-manager';

/**
 * A button component that handles sharing with manager functionality.
 * Shows three states: initial, sharing (brief success state), and shared.
 */
export const ShareWithManagerButton: React.FC<ShareWithManagerButtonProps> = ({
  className,
  disabled = false,
  initialState = 'initial',
  onShare,
  scoreCardId,
  sessionId,
  userId,
}) => {
  // =======================================================================================
  // Use the custom hook to manage the share with manager feature logic and state
  // =======================================================================================
  const { getAriaLabel, getButtonContent, handleClick, shareState } = useShareWithManagerFeature({
    disabled,
    initialState,
    onShare,
    scoreCardId,
    sessionId,
    userId,
  });

  const buttonContent = getButtonContent();

  // ======================================================================================
  // If the feature flag is disabled, render a disabled button
  // with "coming soon" label and appropriate ARIA label
  // ======================================================================================
  if (!FEATURE_FLAGS.ENABLE_SHARE_WITH_MANAGER) {
    return (
      <button
        className={cn(
          getButtonStyles('initial'),
          className,
          'cursor-not-allowed',
          'disabled:pointer-events-auto',
          'enabled:focus-visible:outline-0',
          'enabled:active:border-transparent',
        )}
        onClick={() => {}}
        disabled={true}
        aria-label={`${ARIA_LABELS.SHARE_WITH_MANAGER} (coming soon)`}
        type="button"
      >
        {BUTTON_LABELS.SHARE_WITH_MANAGER}
      </button>
    );
  }

  // ======================================================================================
  // Render the button with dynamic content and styles based on the current state
  // ======================================================================================
  return (
    <button
      aria-label={getAriaLabel()}
      className={cn(getButtonStyles(shareState), className)}
      disabled={disabled || shareState !== 'initial'}
      onClick={handleClick}
      type="button"
    >
      {buttonContent.iconName ? <Icon name={buttonContent.iconName} size={16} /> : null}
      {buttonContent.label}
    </button>
  );
};

ShareWithManagerButton.displayName = 'ShareWithManagerButton';

export default ShareWithManagerButton;
